/**
 * @fileoverview
 * This file contains all the functions that are used to retrieve data from the
 * Embed feed API.
 */

import type { AxiosInstance } from 'axios'

import type {
  Api_ChannelByIdResponse,
  Api_ChannelResponse,
} from '@core/api/types/api_channel'
import type { Api_Feed, FeedApiParams } from '@core/api/types/api_feed'
import type { Api_Playlist_Collection } from '@core/api/types/api_playlist'
import { preventCache } from '@core/api/utils/preventCache'

/**
 * Retrieves a channel by username
 * @param api
 * @returns
 */
export async function getChannel(
  api: AxiosInstance,
  apiCacheNonce: string,
  username: string,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channels/${encodeURIComponent(username)}`,
  )
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get<Api_ChannelResponse>(...preventCache(url.href))
  // Merge domain_assistant_id into channel.config if both exist
  // This is needed because frontend treats domainAssistantId as part of configuration,
  // while backend returns it as a separate field because it's not part of Channel config schema
  if (
    response.data?.business_active === true &&
    response.data.domain_assistant_id &&
    response.data.channel.config
  ) {
    response.data.channel.config = {
      ...response.data.channel.config,
      domain_assistant_id: response.data.domain_assistant_id,
    }
  }
  return response.data
}

/**
 * Retrieves a channel by id
 * @param api
 * @returns
 */
export async function getChannelById(api: AxiosInstance, encodedId: string) {
  const response = await api.get<Api_ChannelByIdResponse>(
    ...preventCache(`/api/channels/by_id/${encodeURIComponent(encodedId)}`),
  )
  return response.data
}

/**
 *
 * @param channel
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedUserTimelineFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { channel, pageSize, firstVideoId }: FeedApiParams,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channels/${encodeURIComponent(channel!)}/timeline_feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get<Api_Feed>(...preventCache(url.href))

  return response.data
}

/**
 *
 * @param api
 * @param playlistId
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedPlaylistFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { channel, playlistId, pageSize, firstVideoId }: FeedApiParams,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/playlists/${encodeURIComponent(playlistId!)}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  channel && url.searchParams.append('channel', channel)
  const response = await api.get<Api_Feed>(...preventCache(url.href))
  return response.data
}

/**
 *
 * @param api
 * @param channel
 * @param hashtagExpression
 * @param pageSize
 * @param firstVideoId
 * @returns
 */
export async function createEmbedUserTimelineHashtagFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { channel, hashtagExpression, pageSize, firstVideoId }: FeedApiParams,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channel_hashtags/${channel}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  hashtagExpression &&
    url.searchParams.append('hashtag_filter_expression', hashtagExpression)
  firstVideoId && url.searchParams.append('first_video_id', firstVideoId)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get<Api_Feed>(...preventCache(url.href))
  return response.data
}

/**
 *
 * @param api
 * @param channel
 * @param pageSize
 * @param skus
 * @returns
 */
export async function createEmbedUserProductSKUFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { channel, pageSize, skus, videoTypes }: FeedApiParams,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channel_product_ext_ids/${channel}/feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  skus && url.searchParams.append('skus', `${skus}`)
  if (videoTypes?.length) {
    url.searchParams.append('video_types[]', videoTypes.join(','))
  }
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get<Api_Feed>(...preventCache(url.href))
  return response.data
}

/**
 *
 * @param api
 * @param apiCacheNonce
 * @param params - id, pageSize, firstPlaylistId, channel
 * @returns
 */
export async function createEmbedPlaylistCollectionFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  {
    id,
    pageSize,
    firstPlaylistId,
    channel,
  }: {
    id: string | undefined
    pageSize?: number
    firstPlaylistId?: string
    channel?: string
  },
) {
  const url = new URL(`${api.defaults.baseURL}/embed/v2/playlist_groups/${id}/`)
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  firstPlaylistId &&
    url.searchParams.append('first_playlist_id', firstPlaylistId)
  url.searchParams.append('n', apiCacheNonce)
  channel && url.searchParams.append('channel', channel)
  const response = await api.get<Api_Playlist_Collection>(
    ...preventCache(url.href),
  )
  return response.data
}

/**
 * Creates a feed with specific videos.
 */
export async function createEmbedUserVideosFeed(
  api: AxiosInstance,
  apiCacheNonce: string,
  { channel, pageSize, videoIds }: FeedApiParams,
) {
  const url = new URL(
    `${api.defaults.baseURL}/embed/v2/channels/${encodeURIComponent(channel!)}/videos_feeds`,
  )
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  url.searchParams.append('n', apiCacheNonce)
  const response = await api.get<Api_Feed>(
    ...preventCache(url.href, {
      params: {
        videos: videoIds,
      },
      /**
       * Simple serializer to handle array params
       * videos[]=abc123&videos[]=def456&videos[]=ghi789
       */
      paramsSerializer: ({ videos }: { videos: string[] }) => {
        return videos.map((v) => `videos[]=${encodeURIComponent(v)}`).join('&')
      },
    }),
  )
  return response.data
}

/**
 * Creates request for next page of given feed
 * @param api
 * @param feed
 * @param pageSize
 * @returns
 */
export async function getMoreFeedItems(
  api: AxiosInstance,
  feed: Api_Feed,
  pageSize?: number,
) {
  const url = new URL(`${api.defaults.baseURL}${feed.next_page}`)
  pageSize && url.searchParams.append('page_size', `${pageSize}`)
  const response = await api.get<Api_Feed>(...preventCache(url.href))
  return response.data
}
